import axios from './axiosInstance';

/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  쇼핑몰 상품연동 상태 관리 (웹)
 * ════════════════════════════════════════════════════════════════════════════════
 *  - 쇼핑몰 고객이 비토리지 버튼을 눌러 유입하면 연동토큰(linkToken)을 localStorage 에 보관한다.
 *  - 로그인 / 회원가입 리디렉션을 거쳐도 연동상태가 유지되어야 하므로 세션이 아닌 localStorage 사용.
 *  - 주문/결제가 완료되거나, 새 상품으로 다시 연동하면 즉시 깨끗하게 제거한다.
 * ════════════════════════════════════════════════════════════════════════════════
 */

export const STORE_LINK_TOKEN_KEY = 'btorageStoreLinkToken';
export const STORE_LINK_COMPANY_KEY = 'btorageStoreLinkCompany';
/** 연동 안내팝업을 이미 보여줬는지 (같은 연동에 대해 1회만 노출) */
export const STORE_LINK_NOTICE_KEY = 'btorageStoreLinkNoticeShown';

export const saveStoreLink = (linkToken, companyName) => {
    try {
        window.localStorage.setItem(STORE_LINK_TOKEN_KEY, linkToken);
        if (companyName) window.localStorage.setItem(STORE_LINK_COMPANY_KEY, companyName);
        window.localStorage.removeItem(STORE_LINK_NOTICE_KEY);
    } catch (e) { /* 저장 실패 무시 */ }
};

export const getStoreLinkToken = () => {
    try {
        return window.localStorage.getItem(STORE_LINK_TOKEN_KEY);
    } catch (e) {
        return null;
    }
};

export const getStoreLinkCompany = () => {
    try {
        return window.localStorage.getItem(STORE_LINK_COMPANY_KEY) || '';
    } catch (e) {
        return '';
    }
};

/** 쇼핑몰 연동 상태인지 여부 (앱설치 배너 등에서 분기용) */
export const hasStoreLink = () => !!getStoreLinkToken();

export const isStoreLinkNoticeShown = () => {
    try {
        return window.localStorage.getItem(STORE_LINK_NOTICE_KEY) === '1';
    } catch (e) {
        return false;
    }
};

export const markStoreLinkNoticeShown = () => {
    try {
        window.localStorage.setItem(STORE_LINK_NOTICE_KEY, '1');
    } catch (e) { /* 무시 */ }
};

/** 연동상태 완전 제거 (로컬 + 서버 보관데이터) */
export const clearStoreLink = (skipServer) => {
    const token = getStoreLinkToken();
    try {
        window.localStorage.removeItem(STORE_LINK_TOKEN_KEY);
        window.localStorage.removeItem(STORE_LINK_COMPANY_KEY);
        window.localStorage.removeItem(STORE_LINK_NOTICE_KEY);
    } catch (e) { /* 무시 */ }

    if (token && !skipServer) {
        axios.post('/storeLink/clear', {linkToken: token}).catch(() => {});
    }
};

/**
 * 연동된 상품데이터 조회
 * @returns {Promise<Object|null>} 만료되었거나 없으면 null
 */
export const fetchStoreLinkedData = async () => {
    const token = getStoreLinkToken();
    if (!token) return null;
    try {
        const res = await axios.get('/storeLink/linkedData', {params: {t: token}});
        return res.data || null;
    } catch (e) {
        // 만료된 토큰은 로컬에서도 정리한다
        clearStoreLink(true);
        return null;
    }
};

/**
 * 주문/결제 완료 통보
 *  - 연동상품을 판매상품으로 적재하고 서버/로컬의 연동데이터를 폐기한다.
 *  - 주문저장 흐름을 방해하지 않도록 실패해도 조용히 넘어간다.
 */
export const completeStoreLink = async (orderNumber, authToken) => {
    const token = getStoreLinkToken();
    if (!token) return;
    try {
        await axios.post('/storeLink/complete', {linkToken: token, orderNumber},
            authToken ? {headers: {Authorization: authToken}} : undefined);
    } catch (e) { /* 무시 */ }
    clearStoreLink(true);
};

/* ════════════════════════════════════════════════════════════════════════════
 *  예상 해외배송료 — 연동상품의 모의 패킹
 * ════════════════════════════════════════════════════════════════════════════
 *  상점사가 상품마다 가로(wid)/세로(ver)/높이(hei)/실중량(weight)을 선택적으로 보낸다.
 *  여러 상품을 한 상자에 담았을 때의 바깥치수를 추정해 배송료조회에 넘긴다.
 *
 *  [선반(층) 쌓기]
 *   바닥에 가장 큰 상품을 깔고 그 위로 층을 올린다.
 *   한 층에 자리가 남으면 작은 상품을 그 옆에 같이 놓는다.
 *
 *     바닥 가로 = 각 상품 '긴 변' 중 가장 큰 값
 *     바닥 세로 = 각 상품 '짧은 변' 중 가장 큰 값
 *     높이      = 각 층 높이의 합 (한 층의 높이 = 그 층에서 가장 높은 상품)
 *     실중량    = 전부 합
 *
 *  [면적/부피를 곱하지 않는다]
 *   "남은 면적이 충분하다" 는 들어간다는 뜻이 아니다.
 *   45x32 바닥에 384cm² 가 남아도 그게 폭 12cm 짜리 띠라면 13cm 상품은 못 들어간다.
 *   그래서 각 층이 '아직 비어있는 직사각형' 을 cm 로 들고 있고,
 *   넣을 때마다 남은 자리를 오른쪽/위쪽 조각으로 잘라 관리한다.
 *   판정은 전부 cm 비교뿐이고 곱셈이 한 군데도 없다.
 *
 *  [왜 긴 변 / 짧은 변으로 정규화하는가]
 *   상점사가 같은 상자를 '45 x 25' 로 보낼 수도 '25 x 45' 로 보낼 수도 있다.
 *   받은 순서대로 max 를 잡으면 같은 상품인데 바닥이 달라진다.
 *   먼저 (긴 변, 짧은 변) 으로 맞춰두면 어느 쪽으로 보내든 결과가 같다.
 *
 *  ※ 빈 자리를 직사각형으로만 잘라 쓰므로 L자로 남는 공간까지는 활용하지 못한다.
 *    즉 실제 숙련된 패킹보다 높이가 같거나 조금 크게 나올 수 있다.
 *    모자라게 안내해 나중에 더 청구하는 것보다 안전한 방향이라 이대로 둔다.
 * ════════════════════════════════════════════════════════════════════════════ */

/** 0 / null / 음수 / 숫자아님을 모두 걸러낸 양수인가 */
const positive = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0;
};

/** 가로/세로/높이/실중량이 모두 유효한 상품인지 */
export const hasFullDimension = (p) =>
    positive(p?.wid) && positive(p?.ver) && positive(p?.hei) && positive(p?.weight);

/** 실중량만이라도 있는 상품인지 (선편은 실무게만으로 조회가 된다) */
export const hasWeightOnly = (p) => positive(p?.weight);

/** 상점사가 부피/중량을 '조금이라도' 보냈는지 (팝업을 띄울지 판단용) */
export const hasAnyDimension = (products) =>
    Array.isArray(products) && products.some((p) =>
        positive(p?.wid) || positive(p?.ver) || positive(p?.hei) || positive(p?.weight));

/** 상품 한 건을 (긴변, 짧은변, 높이) 로 정규화 */
const normalizeItem = (p) => {
    const a = Number(p.wid);
    const b = Number(p.ver);
    return {
        w: Math.max(a, b),
        d: Math.min(a, b),
        h: Number(p.hei),
    };
};

/**
 * 한 층의 빈 자리에 상품을 넣어본다.
 *  넣었으면 true. 판정은 전부 cm 비교다.
 */
const placeOnShelf = (shelf, item) => {
    for (let i = 0; i < shelf.free.length; i++) {
        const free = shelf.free[i];

        // 그대로 / 90도 돌려서 두 자세를 본다
        const poses = [{w: item.w, d: item.d}, {w: item.d, d: item.w}];
        for (const pose of poses) {
            if (pose.w <= free.w && pose.d <= free.d) {
                shelf.free.splice(i, 1);
                // 남은 자리를 오른쪽 조각 / 위쪽 조각으로 나눈다
                if (free.w - pose.w > 0) shelf.free.push({w: free.w - pose.w, d: free.d});
                if (free.d - pose.d > 0) shelf.free.push({w: pose.w, d: free.d - pose.d});
                // 층 높이는 그 층에서 가장 높은 상품을 따른다
                if (item.h > shelf.h) shelf.h = item.h;
                return true;
            }
        }
    }
    return false;
};

/**
 * 연동상품들을 한 상자에 담았을 때의 바깥치수를 추정한다.
 *
 * @returns {{mode:'full', wid:number, ver:number, hei:number, weight:number}}
 *          모든 상품에 가로/세로/높이/실중량이 다 있는 경우
 *          {{mode:'weight', weight:number}}
 *          치수는 빠졌지만 실중량은 전부 있는 경우 (선편만 조회 가능)
 *          {{mode:'none'}}
 *          실중량조차 빠진 상품이 있어 아무것도 계산할 수 없는 경우
 */
export const mergePacking = (products) => {
    if (!Array.isArray(products) || products.length === 0) return {mode: 'none'};

    // 실중량이 하나라도 빠지면 무게합 자체가 틀어지므로 아무것도 내지 않는다
    if (!products.every(hasWeightOnly)) return {mode: 'none'};

    const weight = Math.round(
        products.reduce((sum, p) => sum + Number(p.weight), 0) * 100,
    ) / 100;

    // 치수가 하나라도 빠지면 상자 크기를 알 수 없다. 실중량만 돌려준다.
    if (!products.every(hasFullDimension)) return {mode: 'weight', weight};

    const items = products.map(normalizeItem);

    const wid = Math.ceil(Math.max(...items.map((i) => i.w)));
    const ver = Math.ceil(Math.max(...items.map((i) => i.d)));

    // 높은 상품부터 담아야 낮은 상품이 위층 빈틈을 메운다
    const sorted = items.slice().sort((a, b) => b.h - a.h);

    const shelves = [];
    for (const item of sorted) {
        let placed = false;
        for (const shelf of shelves) {
            if (placeOnShelf(shelf, item)) {
                placed = true;
                break;
            }
        }
        if (!placed) {
            const shelf = {h: 0, free: [{w: wid, d: ver}]};
            placeOnShelf(shelf, item);
            shelves.push(shelf);
        }
    }

    const hei = Math.ceil(shelves.reduce((sum, s) => sum + s.h, 0));

    return {mode: 'full', wid, ver, hei, weight};
};

/** yyyyMMdd(DB 저장형식) -> yyyy-MM-dd(화면 출력형식). 형식이 다르면 원본을 그대로 둔다. */
export const toDashDate = (ymd) => {
    const raw = String(ymd || '').trim();
    if (!/^\d{8}$/.test(raw)) return raw;
    return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
};

/**
 * 주문서 1건의 총 상품금액
 *
 *  주문서에는 6가지 폼양식(장바구니 P00 / 구매대행 P01 / 오프라인 P02 /
 *  이체대행 P03 / 국내배송 P04 / 배송대행 P05)의 상품이 섞여 담긴다.
 *  productsDTO 는 그 전부를 담고 있으므로 종류를 가리지 않고 금액컬럼을 모두 더한다.
 *
 *  - price         상품금액
 *  - mallShipPrice 현지 쇼핑몰 배송비
 *  - fee           수수료
 *
 *  신청취소(IS12)된 상품은 실제로 청구되지 않으므로 제외한다.
 */
export const sumOrderProductPrice = (products) => {
    if (!Array.isArray(products)) return 0;
    return products.reduce((sum, p) => {
        if (p?.prodSt === 'IS12') return sum;
        return sum
            + (Number(p?.price) || 0)
            + (Number(p?.mallShipPrice) || 0)
            + (Number(p?.fee) || 0);
    }, 0);
};

/**
 * 정렬 기준시각 — regTime 과 modiTime 중 더 최신인 값
 *
 *  관리자 메인화면(AdminDashboardPg / MAdminDashboardPg)의 mostRecentDate 와 같은 규칙이다.
 *
 *  Date 로 바꾸지 않고 문자열 그대로 비교한다.
 *   - 서버(CommonEntity)가 두 값 모두 'yyyy-MM-dd HH:mm:ss' 로만 만든다.
 *     자리수가 고정되고 0 으로 채워진 형식이라 사전순 비교가 곧 시간순 비교다.
 *   - new Date('2026-09-20 14:30:00') 은 표준이 아니어서 엔진마다 결과가 다르다.
 *     (사파리는 Invalid Date, Hermes/V8 은 현지시각) 그 차이에 기대지 않는다.
 */
const orderStamp = (v) => {
    if (typeof v !== 'string') return '';
    const s = v.trim().replace('T', ' ');
    if (s === '') return '';
    // 날짜만 온 경우 자리수를 맞춰둔다 (그래야 같은 날 시:분:초 와 비교된다)
    return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s + ' 00:00:00' : s;
};

/**
 * 합배송 선택목록에 보여줄 주문서 최대 개수
 *  목록이 길어지면 카드마다 상품명까지 그리느라 팝업이 눈에 띄게 느려진다.
 *  최신순으로 자르므로 오래된 주문서부터 빠진다.
 */
export const MERGE_LIST_LIMIT = 50;

/** 주문서 하나의 정렬 기준시각 */
export const latestOrderStamp = (o) => {
    const reg = orderStamp(o && o.regTime);
    const modi = orderStamp(o && o.modiTime);
    return modi > reg ? modi : reg;
};

/**
 * 최신순(내림차순) 정렬
 *  기준시각이 같으면 원래 순서를 지킨다.
 *  시각이 없는(빈 문자열) 건은 자연히 맨 뒤로 간다.
 */
export const sortByLatestOrder = (orders) => {
    if (!Array.isArray(orders)) return [];
    return orders
        .map((o, i) => ({o, i, k: latestOrderStamp(o)}))
        .sort((a, b) => (a.k === b.k ? a.i - b.i : (a.k < b.k ? 1 : -1)))
        .map((x) => x.o);
};

/**
 * 합배송 선택목록용 주문서 요약 만들기
 *  화면(StoreLinkPopup)이 필요로 하는 형태로만 추려서 넘긴다.
 */
export const toMergeableOrder = (o) => ({
    orderNumber: o.orderNumber,
    regDate: toDashDate(o.regDate),
    recname: o.recinfoDTO?.recname || '',
    shipco: o.shipco || '',
    totalPrice: sumOrderProductPrice(o.productsDTO),
    products: (o.productsDTO || [])
        .filter((p) => p?.prodSt !== 'IS12')
        .map((p) => ({name: p.name, option: p.option, price: p.price})),
});

/**
 * 연동 상품데이터를 주문서 '구매대행 서비스' 폼 형식으로 변환
 * - 주문서(UserOrderPg / UserMyOrderPg)의 orderProductHandle 항목 구조에 맞춘다.
 */
export const toOrderProductForm = (products) => {
    if (!Array.isArray(products)) return [];
    return products.map(p => ({
        name: p.name || '',
        option: p.option || '',
        quantity: p.quantity != null ? String(p.quantity) : '1',
        price: p.price != null ? String(p.price) : '0',
        mallShipPrice: p.mallShipPrice != null ? String(p.mallShipPrice) : '0',
        url: p.url || '',
    }));
};
